/* eslint-disable @typescript-eslint/no-explicit-any */

import { omit } from 'es-toolkit/compat';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { Service } = protos;

/**
 * Creates a blank service. Provided object must have a weekday!
 * @param {Object} service The service object. Must have a weekday key.
 * @returns {Object} A redux TockApi action.
 */
export function createService(service: any): ApiAction {
  const request = Service.fromObject(omit(service, 'state'));
  return {
    TockApi: {
      postProto: '/service',
      type: AppConstants.SERVICES_CREATE,
      request,
      toast: 'Service created',
    },
  };
}

/**
 * Updates the service
 * @param {Object} serviceChange needs to have an id, and then whatever we want to change.
 * @returns {Object} A redux TockApi action.
 */
export function updateService(serviceChange: any): ApiAction {
  const request = Service.fromObject(omit(serviceChange, 'state'));
  return {
    TockApi: {
      putProto: '/service',
      type: AppConstants.SERVICES_UPDATE,
      request,
      toast: 'Service updated',
    },
  };
}

/**
 * Deletes the provided services
 * @param {Object} service The service to delete. Must have an id and weekday field.
 * @returns {Object} A redux TockApi action.
 */
export function deleteService(service: any): ApiAction {
  return {
    TockApi: {
      deleteProto: `/service/${service.id}`,
      type: AppConstants.SERVICES_DELETE,
      context: { deletedService: service },
    },
  };
}

/**
 * Fetches all services for the business.
 * @returns {Object} A redux TockApi action.
 */
export function fetchAll(): ApiAction {
  return {
    TockApi: {
      getListProto: '/service',
      type: AppConstants.SERVICES_FETCH_ALL,
    },
  };
}

/**
 * Creates a service locally for the given weekday
 * @param {String} weekday: The day of the week for the service
 * @returns {Object} A redux action.
 */
export function createLocalService(weekday: any): BaseTockAction {
  return {
    type: AppConstants.SERVICES_CREATE,
    payload: weekday,
  };
}

/**
 * Updates the service at the given index
 * @param {Object} serviceChange: A key:value mapping of changes to the service
 * @param {Number} index: The index of the service w/i the weekday array
 * @returns {Object} A redux action.
 */
export function updateLocalService(serviceChange: any, index: any): BaseTockAction {
  return {
    type: AppConstants.SERVICES_UPDATE,
    payload: {
      serviceChange,
      index,
    },
  };
}

/**
 * Deletes the service at the given index
 * @param {Object} service: The service to be deleted
 * @param {Number} index: The index of the service w/i the weekday array
 * @returns {Object} A redux action.
 */
export function deleteLocalService(service: any, index: any): BaseTockAction {
  return {
    type: AppConstants.SERVICES_DELETE,
    payload: {
      service,
      index,
    },
  };
}

/**
 * Fetches services for a single date.  This handler is used within TACO businesses to return
 * service hours based on the inventory schedule applied to the supplied date.
 * @returns {Object} A redux TockApi action.
 */
export function fetchForServiceDay(date: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/service/serviceday/${date}`,
      type: AppConstants.SERVICES_FETCH_ALL,
    },
  };
}
